/** @jsx jsx */
import { css, jsx, SerializedStyles } from "@emotion/core"

import React from "react"
import { TextBlock } from "@cd2/text/build/es6"
import { formatDate } from "@cd2/tools"
import { HoverQuoteLoader } from "../hoverLoaders/HoverQuoteLoader"
import { HoverOrderLoader } from "../hoverLoaders/HoverOrderLoader"
import { HoverCustomerLoader } from "../hoverLoaders/HoverCustomerLoader"
import { ConditionalWrapper } from "@cd2/components"
import { QuickToolTip } from "./QuickTooltip"
import { HoverLeadLoader } from "../hoverLoaders/HoverLeadsLoader"
import { brandColors } from "./EOSThemeProvider"
import { formatPrice } from "../utils/utils"
import { HoverUserLoader } from "../hoverLoaders/HoverUserLoader"
import { HoverSubCustomerLoader } from "../hoverLoaders/HoverSubCustomerLoader"
import { HoverPurchaseOrderLoader } from "../hoverLoaders/HoverPurchaseOrderLoader"
import { HoverSupplierLoader } from "../hoverLoaders/HoverSupplierLoader"
import { buildRoute, eRoutes } from "../routes"
import { Link } from "react-router-dom"

interface ILabelInfoProps {
  label?: React.ReactNode
  value?:
    | React.ReactNode
    | { date?: string; format?: string }
    | { price?: number; currency?: string; pennies?: boolean }
    | {
        hover?: {
          model:
            | "quote"
            | "order"
            | "customer"
            | "user"
            | "sub_customer"
            | "purchase_order"
            | "supplier"
          id?: string
        }
      }
  light?: boolean
  inline?: boolean
  labelWidth?: number
  returnNullOnEmpty?: boolean
  tooltip?: React.ReactNode
  style?: SerializedStyles
  link?: { eRoute: eRoutes; params: Record<string, any>; label: string }
}

export function LabelInfo({
  label,
  value,
  light,
  labelWidth,
  inline,
  returnNullOnEmpty,
  tooltip,
  style,
}: ILabelInfoProps) {
  if (returnNullOnEmpty && !value) {
    return null
  }

  return (
    <TextBlock blockSpacing={0}>
      <div
        css={
          inline
            ? css`
                display: flex;
                align-items: baseline;
                gap: 8px;

                > * {
                  flex: 1;
                }
              `
            : undefined
        }
      >
        {label && (
          <div
            css={
              labelWidth
                ? css`
                    max-width: ${labelWidth}px;
                    min-width: ${labelWidth}px !important;
                  `
                : undefined
            }
          >
            <span
              css={css`
                font-size: 12px;
                font-weight: 400;
                color: ${light ? "white" : brandColors.lightNight};
              `}
            >
              {label}
            </span>
          </div>
        )}
        <div
          className="label-info-value"
          css={css(
            css`
              font-size: 14px;
              font-weight: 500;
            `,
            style,
          )}
        >
          {value ? (
            <ConditionalWrapper
              condition={!!tooltip}
              Wrapper={QuickToolTip}
              wrapperProps={{ content: tooltip }}
            >
              {typeof value === "string" ? (
                <span
                  css={css`
                    color: ${light ? "white" : undefined};
                  `}
                >
                  {value}
                </span>
              ) : typeof value === "object" && "date" in value ? (
                value.date ? (
                  formatDate(new Date(value.date), value.format ?? `dd/MM/yyyy, h:mm aa`)
                ) : (
                  <span
                    css={css`
                      color: ${light ? "white" : undefined};
                    `}
                  >
                    -
                  </span>
                )
              ) : typeof value === "object" && "hover" in value ? (
                value.hover?.id ? (
                  <React.Fragment>
                    {value.hover.model === "quote" && <HoverQuoteLoader id={value.hover.id} />}
                    {value.hover.model === "order" && <HoverOrderLoader id={value.hover.id} />}
                    {value.hover.model === "customer" && (
                      <HoverCustomerLoader id={value.hover.id} />
                    )}
                    {value.hover.model === "user" && <HoverUserLoader id={value.hover.id} />}
                    {value.hover.model === "lead" && <HoverLeadLoader id={value.hover.id} />}
                    {value.hover.model === "sub_customer" && (
                      <HoverSubCustomerLoader id={value.hover.id} />
                    )}
                    {value.hover.model === "purchase_order" && (
                      <HoverPurchaseOrderLoader id={value.hover.id} />
                    )}
                    {value.hover.model === "supplier" && (
                      <HoverSupplierLoader id={value.hover.id} />
                    )}
                  </React.Fragment>
                ) : (
                  <span
                    css={css`
                      color: ${light ? "white" : undefined};
                    `}
                  >
                    -
                  </span>
                )
              ) : typeof value === "object" && "price" in value ? (
                value.price ? (
                  <React.Fragment>
                    {formatPrice(value.price, {
                      isPennies: value.pennies,
                      currency: value.currency,
                    })}
                  </React.Fragment>
                ) : (
                  <span
                    css={css`
                      color: ${light ? "white" : undefined};
                    `}
                  >
                    -
                  </span>
                )
              ) : value.link ? (
                <Link to={buildRoute(value.link.eRoute, value.link.params)}>
                  {value.link.label}
                </Link>
              ) : (
                value
              )}
            </ConditionalWrapper>
          ) : (
            <span
              css={css`
                color: ${light ? "white" : undefined};
              `}
            >
              -
            </span>
          )}
        </div>
      </div>
    </TextBlock>
  )
}
